import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useRecurring } from "../hooks/useRecurring";
import { t } from "../i18n";
import { RefreshCw, Plus, CircleCheck, CircleMinus, ArrowUp, ArrowDown, Play, Pause, Trash2, Pencil } from "lucide-react";

const MONTHS = ["Enero","Febrero","Marzo","Abril","Mayo","Junio",
                "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
const fmt = n => "€" + Number(n).toLocaleString("es-ES",{minimumFractionDigits:2,maximumFractionDigits:2});

export default function Recurrentes() {
  const { profile, lang, configLoading } = useAuth();
  const { recurrents, loading, addRecurrent, updateRecurrent, deleteRecurrent, toggleRecurrent, processRecurrents } = useRecurring();
  const c = profile?.config || {};
  const T = (key) => t(lang, key);

  const [type,    setType]    = useState("expense");
  const [cat,     setCat]     = useState("");
  const [desc,    setDesc]    = useState("");
  const [payment, setPayment] = useState("");
  const [amount,  setAmount]  = useState("");
  const [day,     setDay]     = useState("1");
  const [error,   setError]   = useState("");
  const [success, setSuccess] = useState("");

  const cats = type === "income" ? (c.income_cats||[]) : (c.expense_cats||[]);
  const accounts = (c.accounts||[]).map(a => a.name);

  // Campos personalizados: solo en modo negocio, y solo los activados/nombrados
  const isNegocio = profile?.perfil === "negocio";
  const activeCustomFields = isNegocio
    ? (c.custom_fields || [])
        .map((f, i) => ({ ...f, key: `custom${i + 1}` }))
        .filter(f => f.enabled && f.name && f.name.trim())
    : [];
  const [customValues, setCustomValues] = useState({ custom1: "", custom2: "", custom3: "" });
  const [showForm, setShowForm] = useState(false);

  // Edición de un recurrente ya creado
  const [editingId, setEditingId] = useState(null);
  const [editType,    setEditType]    = useState("expense");
  const [editCat,     setEditCat]     = useState("");
  const [editDesc,    setEditDesc]    = useState("");
  const [editPayment, setEditPayment] = useState("");
  const [editAmount,  setEditAmount]  = useState("");
  const [editDay,     setEditDay]     = useState("1");
  const [editCustomValues, setEditCustomValues] = useState({ custom1: "", custom2: "", custom3: "" });
  const [editSaving, setEditSaving] = useState(false);

  function startEditRec(r) {
    setEditingId(r.id);
    setEditType(r.type);
    setEditCat(r.cat || "");
    setEditDesc(r.desc || "");
    setEditPayment(r.payment || "");
    setEditAmount(String(r.amount));
    setEditDay(String(r.day || 1));
    setEditCustomValues({
      custom1: r.custom1 || "", custom2: r.custom2 || "", custom3: r.custom3 || "",
    });
  }

  async function saveEditRec(r) {
    const amt = parseFloat(String(editAmount).replace(",", "."));
    if (!amt || amt <= 0) { setError(lang==="en"?"Enter a valid amount":"Introduce un importe válido"); return; }
    if (!editDesc.trim()) { setError(lang==="en"?"Write a description":"Escribe una descripción"); return; }
    let d = parseInt(editDay, 10);
    if (!d || d < 1) d = 1;
    if (d > 31) d = 31;

    const editActiveCustomFields = isNegocio
      ? (c.custom_fields || [])
          .map((f, i) => ({ ...f, key: `custom${i + 1}` }))
          .filter(f => f.enabled && f.name && f.name.trim())
      : [];
    const customData = {};
    editActiveCustomFields.forEach(f => {
      if (editCustomValues[f.key]) customData[f.key] = editCustomValues[f.key];
    });

    setEditSaving(true);
    try {
      await updateRecurrent(r.id, {
        type: editType, cat: editCat || "General", desc: editDesc.trim(),
        payment: editPayment, amount: amt, day: d, ...customData,
      });
      setEditingId(null);
    } catch (err) {
      console.error("Error al editar el recurrente:", err);
      setError(lang==="en" ? `Something went wrong: ${err.message||err}` : `Algo falló: ${err.message||err}`);
    } finally {
      setEditSaving(false);
    }
  }

  // Procesa recurrentes al cargar
  useEffect(() => {
    if (!loading && recurrents.length > 0) {
      processRecurrents();
    }
  }, [loading]);

  async function handleAdd(e) {
    e.preventDefault();
    setError("");
    const amt = parseFloat(amount.replace(",", "."));
    if (!amt || amt <= 0) { setError("Introduce un importe válido."); return; }
    if (!desc.trim()) { setError("Escribe una descripción."); return; }

    const customData = {};
    activeCustomFields.forEach(f => {
      if (customValues[f.key]) customData[f.key] = customValues[f.key];
    });

    let d = parseInt(day, 10);
    if (!d || d < 1) d = 1;
    if (d > 31) d = 31;

    await addRecurrent({
      type,
      cat: cat || "General",
      desc: desc.trim(),
      payment,
      amount: amt,
      day: d,
      ...customData,
    });

    setSuccess("Entrada recurrente añadida");
    setAmount(""); setDesc(""); setCat(""); setPayment(""); setDay("1");
    setCustomValues({ custom1: "", custom2: "", custom3: "" });
    setTimeout(() => setSuccess(""), 3000);
  }

  if (loading) return <div className="page"><p style={{color:"var(--gray-400)"}}>Cargando...</p></div>;

  const income_label = c.income_label || (lang === "en" ? "Income" : "Ingresos");
  const expense_label = c.expense_label || (lang === "en" ? "Expenses" : "Gastos");

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h2 className="page-title" style={{display:"flex",alignItems:"center",gap:8}}><RefreshCw size={22}/> {lang === "en" ? "Recurring" : "Recurrentes"}</h2>
          <p className="page-sub">{lang === "en" ? "Fixed monthly income and expenses" : "Ingresos y gastos fijos mensuales"}</p>
        </div>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"min(100%,480px)",gap:20,alignItems:"start",justifyContent:"center"}}>

        {/* Formulario */}
        <div className="card">
          {!showForm ? (
            <button className="btn btn-primary" style={{width:"100%",justifyContent:"center",padding:"12px",display:"flex",alignItems:"center",gap:6}}
                    onClick={()=>setShowForm(true)}>
              <Plus size={16}/> {lang === "en" ? "Add entry" : "Añadir entrada"}
            </button>
          ) : (
          <>
          <h3 style={{fontSize:15,fontWeight:600,marginBottom:16,display:"flex",alignItems:"center",gap:6}}>
            <Plus size={16}/> {lang === "en" ? "New recurring entry" : "Nueva entrada recurrente"}
          </h3>

          {error   && <div className="alert alert-error">{error}</div>}
          {success && <div className="alert alert-success" style={{display:"flex",alignItems:"center",gap:6}}><CircleCheck size={14}/> {success}</div>}

          <form onSubmit={handleAdd}>
            <div className="form-group">
              <label className="form-label">{T("add_type")}</label>
              <div style={{display:"flex",gap:10}}>
                {[["income",income_label,CircleCheck],["expense",expense_label,CircleMinus]].map(([v,l,Icon])=>(
                  <label key={v} style={{flex:1,cursor:"pointer"}}>
                    <input type="radio" value={v} checked={type===v} onChange={()=>{setType(v);setCat("");}} style={{display:"none"}}/>
                    <div style={{padding:"10px 14px",borderRadius:"var(--radius-sm)",textAlign:"center",
                                 border:`2px solid ${type===v?"var(--blue)":"var(--border)"}`,
                                 background:type===v?"var(--blue-l)":"white",fontSize:14,fontWeight:500,
                                 color:type===v?"var(--blue)":"var(--gray-700)",display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
                      <Icon size={15}/> {l}
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{T("add_desc")}</label>
              <input className="form-input" value={desc} onChange={e=>setDesc(e.target.value)}
                     placeholder={lang==="en"?"E.g: Salary, Rent, Netflix...":"Ej: Nómina, Alquiler, Netflix..."} required />
            </div>

            <div className="form-group">
              <label className="form-label">{T("add_cat")}</label>
              {configLoading ? (
                <select className="form-select" disabled>
                  <option>{lang==="en" ? "Loading categories..." : "Cargando categorías..."}</option>
                </select>
              ) : (
                <select className="form-select" value={cat} onChange={e=>setCat(e.target.value)}>
                  <option value="">{T("add_cat_placeholder")}</option>
                  {cats.map(c=><option key={c}>{c}</option>)}
                  <option value="Otro">{T("add_other")}</option>
                </select>
              )}
            </div>

            {accounts.length > 0 && (
              <div className="form-group">
                <label className="form-label">{T("add_account")}</label>
                <select className="form-select" value={payment} onChange={e=>setPayment(e.target.value)}>
                  <option value="">{T("add_no_account")}</option>
                  {accounts.map(a=><option key={a}>{a}</option>)}
                </select>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">{T("add_amount")}</label>
              <input className="form-input" type="number" step="0.01" min="0.01"
                     value={amount} onChange={e=>setAmount(e.target.value)} placeholder="0.00" required />
            </div>

            <div className="form-group">
              <label className="form-label">{lang==="en" ? "Day of month" : "Día del mes"}</label>
              <input className="form-input" type="number" min="1" max="31"
                     value={day} onChange={e=>setDay(e.target.value)} placeholder="1" />
              <p className="form-hint">
                {lang==="en"
                  ? "Which day it gets added automatically (e.g. 5 for a rent due on the 5th)."
                  : "Qué día se añade automáticamente (ej: 5 para un alquiler que vence el día 5)."}
              </p>
            </div>

            {activeCustomFields.length > 0 && (
              <div className="form-group">
                <label className="form-label">{lang==="en" ? "Extra details" : "Datos adicionales"}</label>
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  {activeCustomFields.map(f => (
                    <input key={f.key} className="form-input"
                           placeholder={f.name}
                           value={customValues[f.key]}
                           onChange={e=>setCustomValues(v=>({...v,[f.key]:e.target.value}))}/>
                  ))}
                </div>
              </div>
            )}

            <button className="btn btn-primary" style={{width:"100%",justifyContent:"center",padding:"12px",display:"flex",alignItems:"center",gap:6}} type="submit" disabled={configLoading}>
              {configLoading ? (lang==="en"?"Loading...":"Cargando...") : <><Plus size={16}/> {lang === "en" ? "Add recurring" : "Añadir recurrente"}</>}
            </button>
          </form>
          </>
          )}
        </div>

        {/* Lista */}
        <div>
          {recurrents.length === 0 ? (
            <div className="empty">
              <div className="empty-icon"><RefreshCw size={28}/></div>
              <h3>{lang === "en" ? "No recurring entries" : "Sin entradas recurrentes"}</h3>
              <p>{lang === "en" ? "Add fixed monthly income or expenses" : "Añade ingresos o gastos fijos mensuales"}</p>
            </div>
          ) : (
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              {recurrents.map(r => (
                editingId === r.id ? (
                  <div key={r.id} className="card card-sm">
                    <div style={{display:"flex",flexDirection:"column",gap:8}}>
                      <div style={{display:"flex",gap:10}}>
                        {[["income",income_label,CircleCheck],["expense",expense_label,CircleMinus]].map(([v,l,Icon])=>(
                          <label key={v} style={{flex:1,cursor:"pointer"}}>
                            <input type="radio" checked={editType===v} onChange={()=>{setEditType(v);setEditCat("");}} style={{display:"none"}}/>
                            <div style={{padding:"8px 10px",borderRadius:"var(--radius-sm)",textAlign:"center",fontSize:13,
                                         border:`2px solid ${editType===v?"var(--blue)":"var(--border)"}`,
                                         background:editType===v?"var(--blue-l)":"white",
                                         color:editType===v?"var(--blue)":"var(--gray-700)",display:"flex",alignItems:"center",justifyContent:"center",gap:5}}>
                              <Icon size={13}/> {l}
                            </div>
                          </label>
                        ))}
                      </div>
                      <input className="form-input" value={editDesc} onChange={e=>setEditDesc(e.target.value)}
                             placeholder={lang==="en"?"Description":"Descripción"} />
                      <select className="form-select" value={editCat} onChange={e=>setEditCat(e.target.value)}>
                        <option value="">{T("add_cat_placeholder")}</option>
                        {(editType==="income" ? (c.income_cats||[]) : (c.expense_cats||[])).map(cc=><option key={cc}>{cc}</option>)}
                        <option value="Otro">{T("add_other")}</option>
                      </select>
                      {accounts.length > 0 && (
                        <select className="form-select" value={editPayment} onChange={e=>setEditPayment(e.target.value)}>
                          <option value="">{T("add_no_account")}</option>
                          {accounts.map(a=><option key={a}>{a}</option>)}
                        </select>
                      )}
                      <div style={{display:"flex",gap:8}}>
                        <input className="form-input" type="number" step="0.01" min="0.01" style={{flex:2}}
                               value={editAmount} onChange={e=>setEditAmount(e.target.value)} placeholder="0.00" />
                        <input className="form-input" type="number" min="1" max="31" style={{flex:1}}
                               value={editDay} onChange={e=>setEditDay(e.target.value)}
                               placeholder={lang==="en"?"Day":"Día"} title={lang==="en"?"Day of month":"Día del mes"} />
                      </div>
                      {isNegocio && (c.custom_fields||[])
                        .map((f,i)=>({...f,key:`custom${i+1}`}))
                        .filter(f=>f.enabled && f.name && f.name.trim())
                        .map(f=>(
                          <input key={f.key} className="form-input" placeholder={f.name}
                                 value={editCustomValues[f.key]}
                                 onChange={e=>setEditCustomValues(v=>({...v,[f.key]:e.target.value}))}/>
                        ))}
                      <div style={{display:"flex",gap:8}}>
                        <button className="btn btn-primary btn-sm" style={{flex:1,justifyContent:"center"}}
                                disabled={editSaving} onClick={()=>saveEditRec(r)}>
                          {editSaving ? "..." : (lang==="en"?"Save":"Guardar")}
                        </button>
                        <button className="btn btn-outline btn-sm" style={{flex:1,justifyContent:"center"}}
                                onClick={()=>setEditingId(null)}>
                          {lang==="en"?"Cancel":"Cancelar"}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                <div key={r.id} className="card card-sm" style={{opacity:r.active===false?0.5:1}}>
                  <div style={{display:"flex",alignItems:"center",gap:10}}>
                    <span className={`badge ${r.type==="income"?"badge-green":"badge-red"}`} style={{display:"inline-flex",alignItems:"center",gap:4}}>
                      {r.type==="income" ? <ArrowUp size={12}/> : <ArrowDown size={12}/>} {r.type==="income"?income_label:expense_label}
                    </span>
                    <div style={{flex:1}}>
                      <div style={{fontWeight:600,fontSize:14}}>{r.desc}</div>
                      <div style={{fontSize:12,color:"var(--gray-500)"}}>
                        {r.cat}{r.payment?` · ${r.payment}`:""} · {lang==="en"?"day":"día"} {r.day||1}
                      </div>
                    </div>
                    <div style={{fontWeight:700,fontSize:16,color:r.type==="income"?"var(--green)":"var(--red)"}}>
                      {fmt(r.amount)}
                    </div>
                  </div>
                  <div style={{display:"flex",gap:8,marginTop:10,justifyContent:"flex-end"}}>
                    <button
                      className={`btn btn-sm ${r.active===false?"btn-outline":"btn-outline"}`}
                      style={{color:r.active===false?"var(--gray-400)":"var(--green)",borderColor:r.active===false?"var(--gray-300)":"var(--green)",display:"flex",alignItems:"center",gap:5}}
                      onClick={()=>toggleRecurrent(r.id, r.active===false ? true : false)}>
                      {r.active===false
                        ? <><Play size={13}/> {lang==="en"?"Activate":"Activar"}</>
                        : <><Pause size={13}/> {lang==="en"?"Pause":"Pausar"}</>}
                    </button>
                    <button className="btn btn-outline btn-sm" onClick={()=>startEditRec(r)} style={{display:"flex",alignItems:"center"}}>
                      <Pencil size={14}/>
                    </button>
                    <button className="btn btn-danger btn-sm" style={{display:"flex",alignItems:"center"}}
                      onClick={()=>{ if(window.confirm(lang==="en"?"Delete this recurring entry?":"¿Eliminar esta entrada recurrente?")) deleteRecurrent(r.id); }}>
                      <Trash2 size={14}/>
                    </button>
                  </div>
                </div>
                )
              ))}
            </div>
          )}

          {recurrents.length > 0 && (
            <div className="alert alert-info" style={{marginTop:16,fontSize:13,display:"flex",alignItems:"flex-start",gap:8}}>
              <RefreshCw size={15} style={{flexShrink:0,marginTop:1}}/>
              <span>{lang==="en"
                ? "Each entry is added automatically on its own day of the month."
                : "Cada entrada se añade automáticamente en el día del mes que le hayas puesto."}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
